import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, BtnPrimary, BtnOutline, BtnGold, Alert, Spinner, JoursCompteur } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { ModulePersona } from './ModulePersona';
import { ModulePositionnement } from './ModulePositionnement';
import { ModuleGuide } from './ModuleGuide';
import { CFG_RAISON, CFG_VOIX, CFG_GOOGLE, CFG_META, CFG_CLIENTS } from './moduleConfigs';
import { ModuleIdee } from './ModuleIdee';
import { ModuleAcquisition } from './ModuleAcquisition';
import { ModuleReseaux } from './ModuleReseaux';
import { ModuleFidelisation } from './ModuleFidelisation';




// ─── PARSER STRATÉGIE RÉSEAUX SOCIAUX ───
function parseStrategieReseaux(synthese) {
  if (!synthese) return null;
  const r = {
    reseaux: [], ecartes: [], piliers: [],
    semaine: { lun:[], mar:[], mer:[], jeu:[], ven:[] },
    decouverte: { desc:'', formats:'' },
    consideration: { desc:'', formats:'' },
    conversion: { desc:'', formats:'' },
    kpis: [], synthese: ''
  };
  try {
    const keys = ['RS_RESEAUX','RS_ECARTES','RS_PILIER1','RS_PILIER2','RS_PILIER3',
      'RS_PILIER4','RS_PILIER5','RS_SEMAINE_LUN','RS_SEMAINE_MAR','RS_SEMAINE_MER',
      'RS_SEMAINE_JEU','RS_SEMAINE_VEN','RS_DECOUVERTE','RS_CONSIDERATION',
      'RS_CONVERSION','RS_KPI','SYNTHESE'];
    let normalized = synthese;
    // Supprimer le markdown ** que l'IA peut ajouter autour des clés
    normalized = normalized.replace(/\*\*/g, '');
    // Supprimer les --- séparateurs
    normalized = normalized.replace(/---/g, '\n');
    keys.forEach(k => {
      normalized = normalized.replace(new RegExp('\\s+' + k + ':', 'g'), '\n' + k + ':');
    });
    const lines = normalized.split('\n');
    lines.forEach(line => {
      const t = line.trim();
      const get = (key) => t.startsWith(key) ? t.slice(key.length).trim() : null;
      const getArr = (key) => { const v = get(key); return v ? v.split('|').map(x=>x.trim()).filter(Boolean) : null; };

      if (get('RS_RESEAUX:')) {
        r.reseaux = (getArr('RS_RESEAUX:') || []).map(item => {
          // Format possible: "Instagram:6x/semaine:Priorité1" ou "Instagram:6x/semaine:PRIORITÉ_1"
          // Split sur : mais seulement les 3 premiers segments
          const parts = item.split(':');
          const nom = parts[0]?.trim()||'';
          const freq = parts[1]?.trim()||'';
          const prio = (parts[2]||'').trim().replace('PRIORITÉ_','Priorité ').replace('Priorite_','Priorité ') || 'Secondaire';
          return { nom, freq, priorite: prio };
        }).filter(r => r.nom && r.freq); // Exclure les entrées vides ou mal parsées
      }
      if (get('RS_ECARTES:')) {
        r.ecartes = (getArr('RS_ECARTES:') || []).map(item => {
          const idx = item.indexOf('—');
          return { nom: item.slice(0, idx).trim(), raison: item.slice(idx+1).trim() };
        });
      }
      ['RS_PILIER1:','RS_PILIER2:','RS_PILIER3:','RS_PILIER4:','RS_PILIER5:'].forEach((key, i) => {
        const parts = getArr(key);
        if (parts) r.piliers.push({ nom: parts[0]||'', pct: parts[1]||'', obj: parts[2]||'', ex: parts[3]||'' });
      });
      ['lun','mar','mer','jeu','ven'].forEach(jour => {
        const key = 'RS_SEMAINE_' + jour.toUpperCase() + ':';
        const val = get(key);
        if (val) {
          // Format: "Réseau | Format | Objectif | Réseau2 | Format2 | Objectif2"
          // On regroupe par triplets
          const items = val.split('|').map(x => x.trim()).filter(Boolean);
          const posts = [];
          for (let i = 0; i < items.length; i += 3) {
            posts.push({ reseau: items[i]||'', format: items[i+1]||'', theme: items[i+2]||'' });
          }
          r.semaine[jour] = posts;
        }
      });
      if (get('RS_DECOUVERTE:')) {
        const parts = getArr('RS_DECOUVERTE:');
        r.decouverte = { desc: parts?.[0]||'', formats: parts?.[1]||'' };
      }
      if (get('RS_CONSIDERATION:')) {
        const parts = getArr('RS_CONSIDERATION:');
        r.consideration = { desc: parts?.[0]||'', formats: parts?.[1]||'' };
      }
      if (get('RS_CONVERSION:')) {
        const parts = getArr('RS_CONVERSION:');
        r.conversion = { desc: parts?.[0]||'', formats: parts?.[1]||'' };
      }
      if (getArr('RS_KPI:')) r.kpis = getArr('RS_KPI:');
      if (get('SYNTHESE:')) r.synthese = get('SYNTHESE:');
    });
  } catch(e) {}
  return r.reseaux.length > 0 || r.piliers.length > 0 ? r : null;
}

const RS_ICONS = {
  instagram: '📷', linkedin: '💼', facebook: '📘', youtube: '▶️',
  tiktok: '🎵', pinterest: '📌', twitter: '🐦', x: '🐦',
  snapchat: '👻', telegram: '✈️', whatsapp: '💬', threads: '🔗',
  reddit: '🟠', discord: '🎮', twitch: '🟣'
};

const PILIER_COLORS = ['#1E3A5F','#D4AF37','#2E7D32','#DC2626','#9AA3B0'];
const FUNNEL_STYLES = {
  decouverte: { bg:'#E6F1FB', color:'#185FA5', label:'Découverte', question:'Elle ne te connaît pas encore, comment l\'atteindre ?' },
  consideration: { bg:'#FEF3C7', color:'#B45309', label:'Considération', question:'Elle te suit, comment la convaincre que tu es la bonne ?' },
  conversion: { bg:'#DCFCE7', color:'#166534', label:'Conversion', question:'Elle est prête, comment déclencher le passage à l\'action ?' },
};

function FicheReseaux({ synthese }) {
  const ficheRef = useRef(null);
  const data = parseStrategieReseaux(synthese);
  if (!data) return null;

  const handleDownload = () => {
    const el = ficheRef.current;
    if (!el) return;
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    document.head.appendChild(script);
    script.onload = () => {
      window.html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' }).then(canvas => {
        const link = document.createElement('a');
        link.download = 'strategie-reseaux-sociaux.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    };
  };

  const css = `
    .frs { background: white; border: 0.5px solid #E0E5EB; border-radius: 14px; overflow: hidden; font-family: sans-serif; color: #1E3A5F; }
    .frs-header { background: #1E3A5F; padding: 18px 22px; display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
    .frs-title { color: white; font-size: 1.1rem; font-weight: 500; margin: 0 0 2px; }
    .frs-sub { color: rgba(255,255,255,0.4); font-size: 0.55rem; letter-spacing: 2px; margin: 0; }
    .frs-badge { background: #D4AF37; color: #1E3A5F; font-size: 0.58rem; font-weight: 700; padding: 3px 10px; border-radius: 20px; align-self: flex-start; }
    .frs-body { padding: 18px 22px; }
    .frs-lbl { font-size: 0.58rem; color: #9AA3B0; letter-spacing: 2px; margin-bottom: 10px; font-weight: 500; }
    .frs-section { margin-bottom: 20px; }
    .reseaux-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 8px; }
    .reseau-card { background: #F8FAFB; border: 0.5px solid #E0E5EB; border-radius: 10px; padding: 11px 13px; }
    .reseau-name { font-size: 0.82rem; font-weight: 500; color: #1E3A5F; margin-bottom: 4px; }
    .reseau-freq { font-size: 0.72rem; color: #9AA3B0; margin-bottom: 4px; }
    .reseau-why { font-size: 0.68rem; color: #6B7280; line-height: 1.4; margin-bottom: 6px; }
    .p-high { background: #DCFCE7; color: #166534; font-size: 0.6rem; padding: 2px 8px; border-radius: 20px; display: inline-block; }
    .p-mid { background: #FEF3C7; color: #B45309; font-size: 0.6rem; padding: 2px 8px; border-radius: 20px; display: inline-block; }
    .ecartes-box { background: #F8FAFB; border-radius: 9px; padding: 10px 13px; font-size: 0.72rem; color: #6B7280; line-height: 1.6; margin-top: 8px; }
    .piliers-list { display: flex; flex-direction: column; gap: 7px; }
    .pilier { display: flex; gap: 12px; background: #F8FAFB; border-radius: 9px; padding: 10px 13px; align-items: flex-start; }
    .pilier-pct { font-size: 0.9rem; font-weight: 500; color: #1E3A5F; min-width: 36px; text-align: center; }
    .pilier-bar { height: 3px; border-radius: 2px; margin-top: 3px; }
    .pilier-name { font-size: 0.78rem; font-weight: 500; margin-bottom: 2px; }
    .pilier-obj { font-size: 0.7rem; color: #6B7280; line-height: 1.4; margin-bottom: 3px; }
    .pilier-ex { font-size: 0.65rem; color: #9AA3B0; font-style: italic; }
    .cal-table { width: 100%; border-collapse: collapse; font-size: 0.72rem; }
    .cal-th { font-size: 0.6rem; color: #9AA3B0; padding: 6px 8px; text-align: left; border-bottom: 0.5px solid #E0E5EB; font-weight: 500; letter-spacing: 1px; }
    .cal-td { padding: 7px 8px; border-bottom: 0.5px solid #E0E5EB; vertical-align: top; }
    .cal-jour { font-weight: 500; color: #1E3A5F; white-space: nowrap; }
    .cal-tag { display: inline-block; border-radius: 4px; padding: 2px 6px; margin: 1px 2px 1px 0; font-size: 0.65rem; line-height: 1.4; }
    .t-ig { background: #FBEAF0; color: #993556; }
    .t-li { background: #E6F1FB; color: #185FA5; }
    .t-yt { background: #FCEBEB; color: #A32D2D; }
    .t-fb { background: #E6F1FB; color: #185FA5; }
    .t-def { background: #F0F2F5; color: #5A6475; }
    .cal-why { font-size: 0.65rem; color: #9AA3B0; line-height: 1.4; }
    .funnel-list { display: flex; flex-direction: column; gap: 8px; }
    .funnel-item { border-radius: 9px; padding: 11px 14px; border: 0.5px solid #E0E5EB; }
    .funnel-top { display: flex; align-items: center; gap: 10px; margin-bottom: 5px; }
    .funnel-tag { font-size: 0.65rem; padding: 3px 10px; border-radius: 20px; font-weight: 500; white-space: nowrap; }
    .funnel-q { font-size: 0.75rem; font-weight: 500; color: #1E3A5F; }
    .funnel-desc { font-size: 0.72rem; color: #6B7280; line-height: 1.5; margin-bottom: 4px; }
    .funnel-ex { font-size: 0.65rem; color: #9AA3B0; font-style: italic; }
    .kpi-row { display: flex; gap: 8px; flex-wrap: wrap; }
    .kpi-tag { background: #F8FAFB; border: 0.5px solid #E0E5EB; border-radius: 8px; padding: 7px 12px; font-size: 0.72rem; color: #1E3A5F; }
    .frs-footer { display: flex; justify-content: space-between; align-items: center; padding: 13px 22px; border-top: 0.5px solid #E0E5EB; flex-wrap: wrap; gap: 10px; }
    .frs-footer-txt { font-size: 0.58rem; color: #9AA3B0; }
    .frs-dl { display: flex; align-items: center; gap: 6px; background: #1E3A5F; color: white; font-size: 0.72rem; font-weight: 500; padding: 7px 14px; border-radius: 8px; border: none; cursor: pointer; }
    @media (max-width: 500px) { .reseaux-grid { grid-template-columns: 1fr 1fr; } }
  `;

  const getTagClass = (reseau) => {
    const r = (reseau||'').toLowerCase();
    if (r.includes('instagram')) return 't-ig';
    if (r.includes('linkedin')) return 't-li';
    if (r.includes('youtube')) return 't-yt';
    if (r.includes('facebook')) return 't-fb';
    return 't-def';
  };

  const JOURS = [
    { key:'lun', label:'Lundi' },
    { key:'mar', label:'Mardi' },
    { key:'mer', label:'Mercredi' },
    { key:'jeu', label:'Jeudi' },
    { key:'ven', label:'Vendredi' },
  ];

  const FunnelBlock = ({ type, data }) => {
    const style = FUNNEL_STYLES[type];
    if (!style || (!data.desc && !data.formats)) return null;
    return (
      <div className="funnel-item">
        <div className="funnel-top">
          <span className="funnel-tag" style={{ background: style.bg, color: style.color }}>{style.label}</span>
          <span className="funnel-q">{style.question}</span>
        </div>
        <div className="funnel-desc">{data.desc}</div>
        {data.formats && <div className="funnel-ex">Formats : {data.formats}</div>}
      </div>
    );
  };

  return (
    <>
      <style>{css}</style>
      <div ref={ficheRef} className="frs">
        <div className="frs-header">
          <div>
            <p className="frs-title">Stratégie réseaux sociaux</p>
            <p className="frs-sub">PLAN CONTENU 30 JOURS, BRANDSTARTER</p>
          </div>
          <span className="frs-badge">MODULE 08</span>
        </div>

        <div className="frs-body">

          {/* RÉSEAUX */}
          {data.reseaux.length > 0 && (
            <div className="frs-section">
              <div className="frs-lbl">RÉSEAUX RETENUS POUR TON PERSONA ET TA MARQUE</div>
              <div className="reseaux-grid">
                {data.reseaux.map((rs, i) => (
                  <div key={i} className="reseau-card">
                    <div className="reseau-name">
                      {RS_ICONS[(rs.nom||'').toLowerCase()] || '📱'} {rs.nom}
                    </div>
                    <div className="reseau-freq">{rs.freq}</div>
                    <span className={rs.priorite?.toLowerCase().includes('prior') ? 'p-high' : 'p-mid'}>
                      {rs.priorite}
                    </span>
                  </div>
                ))}
              </div>
              {data.ecartes.length > 0 && (
                <div className="ecartes-box">
                  <strong style={{ color:'#1E3A5F' }}>Réseaux écartés : </strong>
                  {data.ecartes.map((e,i) => (
                    <span key={i}>{e.nom}{e.raison ? ` (${e.raison})` : ''}{i < data.ecartes.length-1 ? ', ' : ''}</span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* PILIERS */}
          {data.piliers.length > 0 && (
            <div className="frs-section">
              <div className="frs-lbl">PILIERS DE CONTENU</div>
              <div className="piliers-list">
                {data.piliers.map((p, i) => (
                  <div key={i} className="pilier">
                    <div style={{ flexShrink:0, width:40, textAlign:'center' }}>
                      <div className="pilier-pct">{p.pct}</div>
                      <div className="pilier-bar" style={{ background: PILIER_COLORS[i] }} />
                    </div>
                    <div style={{ flex:1 }}>
                      <div className="pilier-name" style={{ color: PILIER_COLORS[i] }}>{p.nom}</div>
                      <div className="pilier-obj">{p.obj}</div>
                      {p.ex && <div className="pilier-ex">Ex : "{p.ex}"</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CALENDRIER */}
          <div className="frs-section">
            <div className="frs-lbl">CALENDRIER ÉDITORIAL, SEMAINE TYPE</div>
            <table className="cal-table">
              <thead>
                <tr>
                  <th className="cal-th" style={{ width:60 }}>JOUR</th>
                  <th className="cal-th">CONTENU</th>
                  <th className="cal-th">OBJECTIF</th>
                </tr>
              </thead>
              <tbody>
                {JOURS.map(({ key, label }) => (
                  data.semaine[key]?.length > 0 && (
                    <tr key={key}>
                      <td className="cal-td"><div className="cal-jour">{label}</div></td>
                      <td className="cal-td">
                        {data.semaine[key].map((item, i) => (
                          <div key={i}>
                            <span className={`cal-tag ${getTagClass(item.reseau)}`}>
                              {item.reseau}, {item.format}
                            </span>
                          </div>
                        ))}
                      </td>
                      <td className="cal-td">
                        {data.semaine[key].map((item, i) => (
                          <div key={i} className="cal-why">{item.theme}</div>
                        ))}
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>

          {/* FUNNEL */}
          <div className="frs-section">
            <div className="frs-lbl">OBJECTIFS PAR ÉTAPE DU PARCOURS CLIENT</div>
            <div className="funnel-list">
              <FunnelBlock type="decouverte" data={data.decouverte} />
              <FunnelBlock type="consideration" data={data.consideration} />
              <FunnelBlock type="conversion" data={data.conversion} />
            </div>
          </div>

          {/* KPIS */}
          {data.kpis.length > 0 && (
            <div className="frs-section">
              <div className="frs-lbl">KPIs À SUIVRE</div>
              <div className="kpi-row">
                {data.kpis.map((k,i) => <span key={i} className="kpi-tag">📊 {k}</span>)}
              </div>
            </div>
          )}

        </div>

        <div className="frs-footer">
          <span className="frs-footer-txt">Généré par BrandStarter · Stratvente © 2026</span>
          <button className="frs-dl" onClick={handleDownload}>⬇ Télécharger PNG</button>
        </div>
      </div>
    </>
  );
}


// ─── PARSER FICHE IDÉE & VISION (MODULE 1) ───
function parseFicheIdee(synthese) {
  if (!synthese) return null;
  const r = { nomMarque:'', idee:'', probleme:'', legitimite:'', vision:'', synthese:'' };
  try {
    const keys = ['NOM_MARQUE','IDEE','PROBLEME_RESOLU','LEGITIMITE','VISION_3ANS','SYNTHESE'];
    let normalized = synthese;
    keys.forEach(k => {
      normalized = normalized.replace(new RegExp('\\s+' + k + ':', 'g'), '\n' + k + ':');
    });
    const lines = normalized.split('\n');
    lines.forEach(line => {
      const t = line.trim();
      const get = (key) => t.startsWith(key) ? t.slice(key.length).trim() : null;
      if (get('NOM_MARQUE:')) r.nomMarque = get('NOM_MARQUE:');
      if (get('IDEE:')) r.idee = get('IDEE:');
      if (get('PROBLEME_RESOLU:')) r.probleme = get('PROBLEME_RESOLU:');
      if (get('LEGITIMITE:')) r.legitimite = get('LEGITIMITE:');
      if (get('VISION_3ANS:')) r.vision = get('VISION_3ANS:');
      if (get('SYNTHESE:')) r.synthese = get('SYNTHESE:');
    });
  } catch(e) {}
  return r.nomMarque || r.idee ? r : null;
}

function FicheIdee({ synthese }) {
  const ficheRef = useRef(null);
  const data = parseFicheIdee(synthese);
  if (!data) return null;

  const handleDownload = () => {
    const el = ficheRef.current;
    if (!el) return;
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    document.head.appendChild(script);
    script.onload = () => {
      window.html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' }).then(canvas => {
        const link = document.createElement('a');
        link.download = `idee-vision-${(data.nomMarque||'projet').toLowerCase().replace(/\s+/g,'-')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    };
  };

  const css = `
    .fi { background: white; border: 0.5px solid #E0E5EB; border-radius: 14px; overflow: hidden; font-family: sans-serif; color: #1E3A5F; margin-top: 20px; }
    .fi-header { background: #1E3A5F; padding: 18px 22px; display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
    .fi-title { color: white; font-size: 1.15rem; font-weight: 500; margin: 0 0 2px; }
    .fi-sub { color: rgba(255,255,255,0.4); font-size: 0.55rem; letter-spacing: 2px; margin: 0; }
    .fi-badge { background: #D4AF37; color: #1E3A5F; font-size: 0.58rem; font-weight: 700; padding: 3px 10px; border-radius: 20px; align-self: flex-start; }
    .fi-body { padding: 18px 22px; display: flex; flex-direction: column; gap: 12px; }
    .fi-block { background: #F8FAFB; border-radius: 9px; padding: 12px 14px; }
    .fi-lbl { font-size: 0.58rem; color: #9AA3B0; letter-spacing: 2px; margin-bottom: 5px; font-weight: 500; }
    .fi-val { font-size: 0.82rem; color: #1E3A5F; line-height: 1.5; }
    .fi-footer { display: flex; justify-content: space-between; align-items: center; padding: 13px 22px; border-top: 0.5px solid #E0E5EB; flex-wrap: wrap; gap: 10px; }
    .fi-footer-txt { font-size: 0.58rem; color: #9AA3B0; }
    .fi-dl { display: flex; align-items: center; gap: 6px; background: #1E3A5F; color: white; font-size: 0.72rem; font-weight: 500; padding: 7px 14px; border-radius: 8px; border: none; cursor: pointer; }
  `;

  return (
    <>
      <style>{css}</style>
      <div ref={ficheRef} className="fi">
        <div className="fi-header">
          <div>
            <p className="fi-title">{data.nomMarque || 'Ton projet'}</p>
            <p className="fi-sub">IDÉE & VISION, BRANDSTARTER</p>
          </div>
          <span className="fi-badge">MODULE 01</span>
        </div>
        <div className="fi-body">
          {data.idee && <div className="fi-block"><div className="fi-lbl">L'IDÉE</div><div className="fi-val">{data.idee}</div></div>}
          {data.probleme && <div className="fi-block"><div className="fi-lbl">PROBLÈME RÉSOLU</div><div className="fi-val">{data.probleme}</div></div>}
          {data.legitimite && <div className="fi-block"><div className="fi-lbl">POURQUOI TOI</div><div className="fi-val">{data.legitimite}</div></div>}
          {data.vision && <div className="fi-block"><div className="fi-lbl">VISION 3 ANS</div><div className="fi-val">{data.vision}</div></div>}
        </div>
        <div className="fi-footer">
          <span className="fi-footer-txt">Généré par BrandStarter · Stratvente © 2026</span>
          <button className="fi-dl" onClick={handleDownload}>⬇ Télécharger PNG</button>
        </div>
      </div>
    </>
  );
}

// ─── PARSER FICHE PERSONA ───
function parseFichePersona(synthese) {
  if (!synthese) return null;
  const r = {
    prenom:'', age:'', situation:'', etudes:'', metier:'', lieu:'', revenu:'',
    budget:'', horizon:'', dispo:'', canaux:[], frustrations:[], objectifs:[],
    defis:[], kpis:[], citation:'', declencheurs:[], score:0,
    coherenceOk:[], coherenceWarn:[], coherenceErr:[], synthese:''
  };
  try {
    // Normaliser : remplacer les espaces entre champs par des sauts de ligne
    // Gérer le cas où tout est sur une ligne ou plusieurs lignes
    const keys = ['PERSONA_PRENOM','PERSONA_AGE','PERSONA_SITUATION','PERSONA_ETUDES',
      'PERSONA_METIER','PERSONA_LIEU','PERSONA_REVENU','PERSONA_BUDGET','PERSONA_HORIZON',
      'PERSONA_DISPO','PERSONA_CANAUX','PERSONA_FRUSTRATIONS','PERSONA_OBJECTIFS',
      'PERSONA_DEFIS','PERSONA_KPIS','PERSONA_CITATION','PERSONA_DECLENCHEURS',
      'COHERENCE_SCORE','COHERENCE_OK','COHERENCE_WARN','COHERENCE_ERR','SYNTHESE'];
    
    let normalized = synthese;
    keys.forEach(k => {
      normalized = normalized.replace(new RegExp('\\s+' + k + ':', 'g'), '\n' + k + ':');
    });
    
    const lines = normalized.split('\n');
    lines.forEach(line => {
      const trimmed = line.trim();
      const get = (key) => trimmed.startsWith(key) ? trimmed.slice(key.length).trim() : null;
      const getArr = (key) => { const v = get(key); return v ? v.split('|').map(x=>x.trim()).filter(Boolean) : null; };
      const v = (key) => get(key);
      
      if (v('PERSONA_PRENOM:')) r.prenom = v('PERSONA_PRENOM:');
      if (v('PERSONA_AGE:')) r.age = v('PERSONA_AGE:');
      if (v('PERSONA_SITUATION:')) r.situation = v('PERSONA_SITUATION:');
      if (v('PERSONA_ETUDES:')) r.etudes = v('PERSONA_ETUDES:');
      if (v('PERSONA_METIER:')) r.metier = v('PERSONA_METIER:');
      if (v('PERSONA_LIEU:')) r.lieu = v('PERSONA_LIEU:');
      if (v('PERSONA_REVENU:')) r.revenu = v('PERSONA_REVENU:');
      if (v('PERSONA_BUDGET:')) r.budget = v('PERSONA_BUDGET:');
      if (v('PERSONA_HORIZON:')) r.horizon = v('PERSONA_HORIZON:');
      if (v('PERSONA_DISPO:')) r.dispo = v('PERSONA_DISPO:');
      if (v('PERSONA_CANAUX:')) r.canaux = v('PERSONA_CANAUX:').split(/[,|]/).map(x=>x.trim()).filter(Boolean);
      if (getArr('PERSONA_FRUSTRATIONS:')) r.frustrations = getArr('PERSONA_FRUSTRATIONS:');
      if (getArr('PERSONA_OBJECTIFS:')) r.objectifs = getArr('PERSONA_OBJECTIFS:');
      if (getArr('PERSONA_DEFIS:')) r.defis = getArr('PERSONA_DEFIS:');
      if (getArr('PERSONA_KPIS:')) r.kpis = getArr('PERSONA_KPIS:');
      if (v('PERSONA_CITATION:')) r.citation = v('PERSONA_CITATION:').replace(/^[""]|[""]$/g,'');
      if (getArr('PERSONA_DECLENCHEURS:')) r.declencheurs = getArr('PERSONA_DECLENCHEURS:');
      if (v('COHERENCE_SCORE:')) r.score = parseInt(v('COHERENCE_SCORE:')) || 0;
      if (getArr('COHERENCE_OK:')) r.coherenceOk = getArr('COHERENCE_OK:');
      if (getArr('COHERENCE_WARN:')) r.coherenceWarn = getArr('COHERENCE_WARN:');
      if (getArr('COHERENCE_ERR:')) r.coherenceErr = getArr('COHERENCE_ERR:');
      if (v('SYNTHESE:')) r.synthese = v('SYNTHESE:');
    });
  } catch(e) {}
  return r.prenom || r.age ? r : null;
}

// ─── COMPOSANT FICHE PERSONA ───
function FichePersona({ synthese }) {
  const ficheRef = useRef(null);
  const data = parseFichePersona(synthese);
  if (!data) return null;

  const initiales = data.prenom
    ? data.prenom.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)
    : 'P';

  const scoreColor = data.score >= 80 ? '#2E7D32' : data.score >= 60 ? '#B45309' : '#DC2626';

  const handleDownload = () => {
    const el = ficheRef.current;
    if (!el) return;
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
    document.head.appendChild(script);
    script.onload = () => {
      window.html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff' }).then(canvas => {
        const link = document.createElement('a');
        link.download = `persona-${(data.prenom||'client').toLowerCase().replace(/\s+/g,'-')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      });
    };
  };

  const css = `
    .fp { background: white; border: 0.5px solid #E0E5EB; border-radius: 14px; overflow: hidden; margin-top: 20px; color: #1E3A5F; font-family: sans-serif; }
    .fp-header { background: #1E3A5F; padding: 18px 22px; display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
    .fp-avatar { width: 48px; height: 48px; border-radius: 50%; background: #D4AF37; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 500; color: #1E3A5F; flex-shrink: 0; }
    .fp-htitle { color: white; font-size: 1.1rem; font-family: Georgia,serif; margin: 0 0 2px; }
    .fp-hsub { color: rgba(255,255,255,0.4); font-size: 0.55rem; letter-spacing: 2px; margin: 0; }
    .fp-badge { background: #D4AF37; color: #1E3A5F; font-size: 0.58rem; font-weight: 700; padding: 3px 10px; border-radius: 20px; align-self: flex-start; }
    .fp-body { padding: 18px 22px; }
    .fp-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
    .fp-full { margin-bottom: 14px; }
    .fp-lbl { font-size: 0.58rem; color: #9AA3B0; letter-spacing: 2px; margin-bottom: 8px; font-weight: 500; }
    .fp-card { background: #F8FAFB; border-radius: 9px; padding: 11px 13px; }
    .fp-row { display: flex; justify-content: space-between; align-items: center; padding: 4px 0; border-bottom: 0.5px solid #E0E5EB; font-size: 0.78rem; }
    .fp-row:last-child { border-bottom: none; }
    .fp-rl { color: #9AA3B0; }
    .fp-rv { color: #1E3A5F; font-weight: 500; }
    .fp-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 10px; }
    .fp-stat { background: #F8FAFB; border-radius: 9px; padding: 10px; text-align: center; }
    .fp-stat-val { font-size: 1.1rem; font-weight: 500; color: #1E3A5F; }
    .fp-stat-lbl { font-size: 0.58rem; color: #9AA3B0; margin-top: 2px; }
    .fp-dot-item { display: flex; gap: 8px; align-items: flex-start; padding: 5px 0; border-bottom: 0.5px solid #E0E5EB; font-size: 0.75rem; color: #1E3A5F; line-height: 1.5; }
    .fp-dot-item:last-child { border-bottom: none; }
    .fp-dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; margin-top: 5px; }
    .fp-tag { display: inline-flex; background: #F0F2F5; border: 0.5px solid #E0E5EB; border-radius: 20px; padding: 3px 9px; font-size: 0.7rem; color: #5A6475; margin: 2px; }
    .fp-quote { border-left: 2px solid #D4AF37; padding: 10px 14px; background: #F8FAFB; border-radius: 0 9px 9px 0; font-size: 0.78rem; color: #5A6475; font-style: italic; line-height: 1.6; }
    .fp-coherence { border: 0.5px solid #E0E5EB; border-radius: 9px; overflow: hidden; }
    .fp-coh-head { display: flex; align-items: center; justify-content: space-between; padding: 10px 13px; background: #F8FAFB; border-bottom: 0.5px solid #E0E5EB; }
    .fp-coh-title { font-size: 0.78rem; font-weight: 500; color: #1E3A5F; }
    .fp-score-wrap { display: flex; align-items: center; gap: 8px; }
    .fp-score-bar { height: 5px; width: 70px; background: #E0E5EB; border-radius: 3px; overflow: hidden; }
    .fp-coh-item { display: flex; gap: 10px; padding: 8px 13px; border-bottom: 0.5px solid #E0E5EB; font-size: 0.75rem; align-items: flex-start; line-height: 1.5; color: #1E3A5F; }
    .fp-coh-item:last-child { border-bottom: none; }
    .fp-footer { display: flex; justify-content: space-between; align-items: center; padding: 13px 22px; border-top: 0.5px solid #E0E5EB; flex-wrap: wrap; gap: 10px; }
    .fp-dl { display: flex; align-items: center; gap: 6px; background: #1E3A5F; color: white; font-size: 0.72rem; font-weight: 500; padding: 7px 14px; border-radius: 8px; border: none; cursor: pointer; }
    @media (max-width: 500px) { .fp-grid2 { grid-template-columns: 1fr; } .fp-stats { grid-template-columns: repeat(3,1fr); } }
  `;

  return (
    <>
      <style>{css}</style>
      <div ref={ficheRef} className="fp">
        <div className="fp-header">
          <div style={{ display:'flex', alignItems:'center', gap:12, flex:1 }}>
            <div className="fp-avatar">{initiales}</div>
            <div>
              <p className="fp-htitle">{data.prenom || 'Persona client'}</p>
              <p className="fp-hsub">PERSONA CLIENT IDÉAL, BRANDSTARTER</p>
            </div>
          </div>
          <span className="fp-badge">MODULE 02</span>
        </div>

        <div className="fp-body">

          {/* PROFIL + STATS */}
          <div className="fp-grid2">
            <div>
              <div className="fp-lbl">PROFIL</div>
              <div className="fp-card">
                {data.age && <div className="fp-row"><span className="fp-rl">Âge</span><span className="fp-rv">{data.age}</span></div>}
                {data.situation && <div className="fp-row"><span className="fp-rl">Situation</span><span className="fp-rv">{data.situation}</span></div>}
                {data.etudes && <div className="fp-row"><span className="fp-rl">Niveau d'études</span><span className="fp-rv">{data.etudes}</span></div>}
                {data.metier && <div className="fp-row"><span className="fp-rl">Métier</span><span className="fp-rv">{data.metier}</span></div>}
                {data.lieu && <div className="fp-row"><span className="fp-rl">Lieu</span><span className="fp-rv">{data.lieu}</span></div>}
                {data.revenu && <div className="fp-row"><span className="fp-rl">Revenu</span><span className="fp-rv">{data.revenu}</span></div>}
              </div>
            </div>
            <div>
              <div className="fp-lbl">DONNÉES CLÉS</div>
              <div className="fp-stats">
                <div className="fp-stat"><div className="fp-stat-val">{data.budget||'—'}</div><div className="fp-stat-lbl">Budget max</div></div>
                <div className="fp-stat"><div className="fp-stat-val">{data.horizon||'—'}</div><div className="fp-stat-lbl">Horizon</div></div>
                <div className="fp-stat"><div className="fp-stat-val">{data.dispo||'—'}</div><div className="fp-stat-lbl">Dispo/sem</div></div>
              </div>
              {data.canaux.length > 0 && <>
                <div className="fp-lbl" style={{ marginTop:10 }}>CANAUX UTILISÉS</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                  {data.canaux.map((c,i) => <span key={i} className="fp-tag">{c}</span>)}
                </div>
              </>}
            </div>
          </div>

          {/* FRUSTRATIONS + OBJECTIFS */}
          <div className="fp-grid2">
            <div>
              <div className="fp-lbl">FRUSTRATIONS</div>
              <div className="fp-card">
                {data.frustrations.map((f,i) => (
                  <div key={i} className="fp-dot-item">
                    <div className="fp-dot" style={{ background:'#D4AF37' }}></div>{f}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="fp-lbl">OBJECTIFS</div>
              <div className="fp-card">
                {data.objectifs.map((o,i) => (
                  <div key={i} className="fp-dot-item">
                    <div className="fp-dot" style={{ background:'#2E7D32' }}></div>{o}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* DÉFIS + KPIS */}
          <div className="fp-grid2">
            <div>
              <div className="fp-lbl">DÉFIS PRINCIPAUX</div>
              <div className="fp-card">
                {data.defis.map((d,i) => (
                  <div key={i} className="fp-dot-item">
                    <div className="fp-dot" style={{ background:'#D4AF37' }}></div>{d}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="fp-lbl">KPIS PERSONNELS</div>
              <div className="fp-card">
                {data.kpis.map((k,i) => (
                  <div key={i} className="fp-dot-item">
                    <div className="fp-dot" style={{ background:'#1E3A5F' }}></div>{k}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CITATION */}
          {data.citation && (
            <div className="fp-full">
              <div className="fp-lbl">SA PHRASE CLÉ</div>
              <div className="fp-quote">"{data.citation}"</div>
            </div>
          )}

          {/* COHÉRENCE IA */}
          {(data.coherenceOk.length > 0 || data.coherenceWarn.length > 0 || data.coherenceErr.length > 0) && (
            <div className="fp-full">
              <div className="fp-lbl">ANALYSE DE COHÉRENCE, IA STRATVENTE</div>
              <div className="fp-coherence">
                <div className="fp-coh-head">
                  <span className="fp-coh-title">Score de cohérence globale</span>
                  <div className="fp-score-wrap">
                    <div className="fp-score-bar">
                      <div style={{ height:'100%', width:`${data.score}%`, background: scoreColor, borderRadius:3 }} />
                    </div>
                    <span style={{ fontSize:'0.75rem', fontWeight:500, color: scoreColor }}>{data.score} / 100</span>
                  </div>
                </div>
                {data.coherenceOk.map((t,i) => (
                  <div key={i} className="fp-coh-item">
                    <span style={{ color:'#2E7D32', flexShrink:0 }}>✓</span><span>{t}</span>
                  </div>
                ))}
                {data.coherenceWarn.map((t,i) => (
                  <div key={i} className="fp-coh-item">
                    <span style={{ color:'#B45309', flexShrink:0 }}>⚠</span><span>{t}</span>
                  </div>
                ))}
                {data.coherenceErr.map((t,i) => (
                  <div key={i} className="fp-coh-item">
                    <span style={{ color:'#DC2626', flexShrink:0 }}>✗</span><span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* DÉCLENCHEURS */}
          {data.declencheurs.length > 0 && (
            <div className="fp-full">
              <div className="fp-lbl">CE QUI LE/LA CONVAINC D'ACHETER</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {data.declencheurs.map((d,i) => <span key={i} className="fp-tag">{d}</span>)}
              </div>
            </div>
          )}

        </div>

        <div className="fp-footer">
          <span style={{ fontSize:'0.58rem', color:'#9AA3B0' }}>Généré par BrandStarter · Stratvente © 2026</span>
          <button className="fp-dl" onClick={handleDownload}>⬇ Télécharger PNG</button>
        </div>
      </div>
    </>
  );
}


// ─── PARSER SYNTHÈSE IDENTITÉ VISUELLE ───
function parseSyntheseIdentite(synthese) {
  if (!synthese) return null;
  const result = { nomMarque: '', palette: [], typoTitre: '', typoCorps: '', logos: [], ambiance: [], synthese: '' };
  try {
    const lines = synthese.split('\n');
    lines.forEach(line => {
      if (line.startsWith('NOM_MARQUE:')) result.nomMarque = line.replace('NOM_MARQUE:', '').trim();
      if (line.startsWith('PALETTE:')) {
        result.palette = line.replace('PALETTE:', '').split('|').map(p => {
          const parts = p.trim().split(':');
          const nom = parts[0] ? parts[0].trim() : '';
          const hex = parts[1] ? '#' + parts[1].trim().replace('#', '') : '#CCCCCC';
          return { nom, hex };
        }).filter(p => p.nom);
      }
      if (line.startsWith('TYPO_TITRE:')) result.typoTitre = line.replace('TYPO_TITRE:', '').trim();
      if (line.startsWith('TYPO_CORPS:')) result.typoCorps = line.replace('TYPO_CORPS:', '').trim();
      if (line.startsWith('LOGO1:') || line.startsWith('LOGO2:') || line.startsWith('LOGO3:')) {
        const val = line.replace(/LOGO[123]:/, '').trim();
        const parts = val.split('|');
        const style = parts[0] ? parts[0].trim().toLowerCase() : 'minimaliste';
        const desc = parts[1] ? parts[1].trim() : '';
        result.logos.push({ style, desc });
      }
      if (line.startsWith('AMBIANCE:')) {
        result.ambiance = line.replace('AMBIANCE:', '').split(',').map(a => a.trim()).filter(Boolean);
      }
      if (line.startsWith('SYNTHESE:')) result.synthese = line.replace('SYNTHESE:', '').trim();
    });
  } catch(e) {}
  return result;
}

// ─── GÉNÉRATEUR DE LOGOS SVG ───
function generateLogoSVG(style, nomMarque, couleur1, couleur2, index) {
  const initiale = nomMarque ? nomMarque[0].toUpperCase() : 'M';
  const initiales = nomMarque ? nomMarque.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() : 'MM';
  const c1 = couleur1 || '#1E3A5F';
  const c2 = couleur2 || '#D4AF37';

  const logos = {
    organique: `<svg viewBox="0 0 56 56" width="56" height="56" xmlns="http://www.w3.org/2000/svg">
      <circle cx="28" cy="28" r="26" fill="${c2}" opacity="0.15"/>
      <circle cx="28" cy="28" r="26" fill="none" stroke="${c1}" stroke-width="1.5"/>
      <path d="M20 30 C20 20 28 15 36 20 C36 36 28 40 20 36 Z" fill="${c1}" opacity="0.8"/>
      <text x="28" y="47" text-anchor="middle" font-family="Georgia,serif" font-size="7" fill="${c1}" font-weight="600">${initiale}</text>
    </svg>`,

    geometrique: `<svg viewBox="0 0 56 56" width="56" height="56" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="4" width="48" height="48" rx="6" fill="${c1}"/>
      <polygon points="28,12 44,40 12,40" fill="none" stroke="${c2}" stroke-width="2"/>
      <circle cx="28" cy="30" r="4" fill="${c2}"/>
    </svg>`,

    typographique: `<svg viewBox="0 0 56 56" width="56" height="56" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="52" height="52" rx="4" fill="white" stroke="${c1}" stroke-width="1.5"/>
      <text x="28" y="33" text-anchor="middle" font-family="Georgia,serif" font-size="24" fill="${c1}" font-weight="700">${initiales}</text>
      <line x1="10" y1="38" x2="46" y2="38" stroke="${c2}" stroke-width="2"/>
    </svg>`,

    embleme: `<svg viewBox="0 0 56 56" width="56" height="56" xmlns="http://www.w3.org/2000/svg">
      <path d="M28 4 L50 16 L50 36 C50 46 28 54 28 54 C28 54 6 46 6 36 L6 16 Z" fill="${c1}"/>
      <text x="28" y="32" text-anchor="middle" font-family="Georgia,serif" font-size="18" fill="${c2}" font-weight="700">${initiale}</text>
    </svg>`,

    minimaliste: `<svg viewBox="0 0 56 56" width="56" height="56" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="4" width="48" height="48" rx="10" fill="${c2}"/>
      <text x="28" y="36" text-anchor="middle" font-family="Georgia,serif" font-size="22" fill="${c1}" font-weight="700">${initiale}</text>
    </svg>`,
  };

  return logos[style] || logos['minimaliste'];
}

const LOGO_LABELS = {
  organique: 'Organique',
  geometrique: 'Géométrique',
  typographique: 'Typographique',
  embleme: 'Emblème',
  minimaliste: 'Minimaliste',
};

// ─── COMPOSANT PLAQUETTE ───
function PlaquetteIdentite({ synthese, prenom }) {
  const [logoChoisi, setLogoChoisi] = useState(0);
  const plaqRef = useRef(null);
  const data = parseSyntheseIdentite(synthese);
  if (!data || !data.palette.length) return null;

  const nomMarque = data.nomMarque || prenom || 'Ma marque';
  const couleur1 = data.palette[0]?.hex || '#1E3A5F';
  const couleur2 = data.palette[1]?.hex || '#D4AF37';
  const couleur3 = data.palette[2]?.hex || '#F0EBE3';

  // Assurer qu'on a 3 logos
  const logos = data.logos.length >= 3 ? data.logos : [
    { style: 'organique', desc: 'Forme naturelle et organique' },
    { style: 'typographique', desc: 'Monogramme épuré et élégant' },
    { style: 'embleme', desc: 'Symbole fort et mémorable' },
  ];

  const handleDownload = async () => {
    try {
      const el = plaqRef.current;
      if (!el) return;
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
      document.head.appendChild(script);
      script.onload = () => {
        window.html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#1E3A5F' }).then(canvas => {
          const link = document.createElement('a');
          link.download = `identite-${nomMarque.toLowerCase().replace(/\s+/g, '-')}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        });
      };
    } catch(e) { alert('Téléchargement indisponible'); }
  };

  return (
    <div ref={plaqRef} style={{ background: '#1E3A5F', borderRadius: 16, padding: 28, marginTop: 20, marginBottom: 8 }}>

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(255,255,255,0.1)', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ color: 'white', fontFamily: 'Georgia,serif', fontSize: '1.35rem', marginBottom: 3 }}>{nomMarque}</div>
          <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.58rem', letterSpacing: '2px', fontFamily: 'sans-serif' }}>IDENTITÉ DE MARQUE, BRANDSTARTER</div>
        </div>
        <span style={{ background: '#D4AF37', color: '#1E3A5F', fontSize: '0.58rem', fontWeight: 700, padding: '4px 12px', borderRadius: 20, letterSpacing: '1px', fontFamily: 'sans-serif', alignSelf: 'flex-start' }}>
          IDENTITÉ VISUELLE
        </span>
      </div>

      {/* LIGNE 1 : PALETTE + TYPO */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* PALETTE */}
        <div>
          <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', fontFamily: 'sans-serif', marginBottom: 10 }}>PALETTE</div>
          <div style={{ display: 'flex', gap: 7 }}>
            {data.palette.slice(0, 4).map((c, i) => (
              <div key={i} style={{ flex: 1, minWidth: 0 }}>
                <div style={{ height: 44, borderRadius: 8, background: c.hex, border: '1px solid rgba(255,255,255,0.1)' }} />
                <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', marginTop: 4, fontFamily: 'sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.nom}</div>
                <div style={{ fontSize: '0.57rem', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{c.hex}</div>
              </div>
            ))}
          </div>
        </div>

        {/* TYPO */}
        <div>
          <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', fontFamily: 'sans-serif', marginBottom: 10 }}>TYPOGRAPHIES</div>
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: 12, height: '100%' }}>
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', fontFamily: 'sans-serif', marginBottom: 3 }}>TITRES</div>
              <div style={{ fontSize: '1rem', color: 'white', fontFamily: 'Georgia,serif', lineHeight: 1.2 }}>{data.typoTitre || 'Playfair Display'}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', fontFamily: 'sans-serif', marginBottom: 3 }}>CORPS</div>
              <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.65)', fontFamily: 'sans-serif' }}>{data.typoCorps || 'DM Sans'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* LIGNE 2 : APERÇU NOM + AMBIANCE */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* APERÇU NOM */}
        <div>
          <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', fontFamily: 'sans-serif', marginBottom: 10 }}>APERÇU NOM DE MARQUE</div>
          <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: '14px 12px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1rem', color: couleur2, marginBottom: 3 }}>{nomMarque}</div>
            <div style={{ width: 32, height: 1, background: couleur1, margin: '6px auto' }} />
            <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.3)', fontFamily: 'sans-serif', letterSpacing: '2px' }}>{data.ambiance[0]?.toUpperCase() || 'MARQUE'}</div>
          </div>
        </div>

        {/* AMBIANCE */}
        <div>
          <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', fontFamily: 'sans-serif', marginBottom: 10 }}>AMBIANCE</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {data.ambiance.slice(0, 4).map((a, i) => (
              <span key={i} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 20, padding: '4px 10px', fontSize: '0.68rem', color: 'rgba(255,255,255,0.65)', fontFamily: 'sans-serif' }}>{a}</span>
            ))}
          </div>
          {logos[0]?.desc && (
            <>
              <div style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px', fontFamily: 'sans-serif', marginBottom: 4 }}>DIRECTION LOGO</div>
              <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.6, fontFamily: 'sans-serif' }}>
                {logos[logoChoisi]?.desc?.slice(0, 100)}
              </p>
            </>
          )}
        </div>
      </div>

      {/* LOGOS */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '2px', fontFamily: 'sans-serif', marginBottom: 10 }}>
          PROPOSITIONS DE LOGOS, CLIQUEZ POUR CHOISIR
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {logos.slice(0, 3).map((logo, i) => (
            <div key={i} onClick={() => setLogoChoisi(i)} style={{
              background: logoChoisi === i ? 'rgba(212,175,55,0.1)' : 'rgba(255,255,255,0.05)',
              border: logoChoisi === i ? '2px solid #D4AF37' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10, padding: '14px 10px', textAlign: 'center', cursor: 'pointer',
              transition: 'all 0.2s'
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, height: 56, alignItems: 'center' }}
                dangerouslySetInnerHTML={{ __html: generateLogoSVG(logo.style, nomMarque, couleur1, couleur2, i) }}
              />
              <div style={{ fontSize: '0.65rem', color: logoChoisi === i ? '#D4AF37' : 'rgba(255,255,255,0.55)', fontFamily: 'sans-serif', fontWeight: logoChoisi === i ? 600 : 400 }}>
                {LOGO_LABELS[logo.style] || logo.style}
              </div>
              {logoChoisi === i && (
                <div style={{ fontSize: '0.55rem', color: '#D4AF37', marginTop: 3, fontFamily: 'sans-serif' }}>✓ Sélectionné</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)', flexWrap: 'wrap', gap: 10 }}>
        <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.18)', fontFamily: 'sans-serif' }}>
          Généré par BrandStarter · Stratvente © 2026
        </span>
        <button onClick={handleDownload} style={{
          background: '#D4AF37', color: '#1E3A5F', fontSize: '0.72rem', fontWeight: 700,
          padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'sans-serif'
        }}>
          ⬇ Télécharger PNG
        </button>
      </div>
    </div>
  );
}

// ─── CONTENU PÉDAGOGIQUE PAR MODULE ───
const MODULE_CONFIG = {
  13: { titre: 'Premiers clients', emoji: '🤝', avecIA: true, intro: '', pedagogie: null, questions: [] },
  12: { titre: 'Publicité Meta', emoji: '📣', avecIA: true, intro: '', pedagogie: null, questions: [] },
  11: { titre: 'Google Business Profile', emoji: '📍', avecIA: true, intro: '', pedagogie: null, questions: [] },
  1: {
    titre: 'Ton idée & ta vision', emoji: '💡', avecIA: true,
    intro: 'Avant tout logo ou communication, clarifions ce que tu fais vraiment et pourquoi c\'est important. La clarté est ton meilleur atout commercial.',
    pedagogie: null,
    questions: [
      { label: 'Décris ton idée en une phrase simple', placeholder: 'Je propose des ateliers de cuisine végétale pour les familles actives...' },
      { label: 'Quel problème concret est-ce que tu résous ?', placeholder: 'Les familles manquent de temps et d\'idées de repas sains...' },
      { label: 'Pourquoi toi et pas quelqu\'un d\'autre ?', placeholder: 'J\'ai 10 ans d\'expérience en nutrition et une approche unique...' },
      { label: 'Ta vision dans 3 ans', placeholder: 'Une école de cuisine végétale reconnue avec 5 formateurs...' },
    ],
  },
  2: {
    titre: 'Ton persona client idéal', emoji: '🎯', avecIA: true,
    intro: 'L\'IA va t\'aider à construire une fiche persona précise. Elle sera exigeante, c\'est pour ton bien.',
    pedagogie: {
      concept: {
        titre: 'C\'est quoi un persona ?',
        texte: 'Un persona est une représentation fictive mais ultra-réaliste de ton client idéal. Ce n\'est pas juste "femme de 35 ans", c\'est une personne avec une vie, des frustrations, des rêves et des objections précises.',
        warning: 'L\'erreur n°1 : vouloir s\'adresser à tout le monde. Une cible floue = une communication floue = zéro vente.'
      },
      framework: {
        titre: 'Les 6 dimensions d\'un persona solide',
        items: [
          { icon: '👤', label: 'Profil', desc: 'Âge, situation pro, niveau de revenus, mode de vie' },
          { icon: '😤', label: 'Sa douleur principale', desc: 'Ce qui le frustre, l\'empêche de dormir, lui coûte du temps ou de l\'argent' },
          { icon: '✨', label: 'Son désir profond', desc: 'Le résultat qu\'il veut vraiment obtenir, pas juste ton service' },
          { icon: '🚫', label: 'Ses objections', desc: 'Pourquoi il pourrait NE PAS acheter chez toi' },
          { icon: '📱', label: 'Où le trouver', desc: 'Réseaux, événements, groupes, podcasts qu\'il consomme' },
          { icon: '💬', label: 'Comment il parle', desc: 'Les mots exacts qu\'il utilise pour décrire son problème' },
        ]
      },
      exemple: {
        titre: 'Exemple concret, Sophie',
        texte: 'Sophie, 38 ans, chef de projet en PME, 2 enfants. Elle veut lancer son activité de coaching mais manque de temps et de confiance. Sa douleur : elle ne sait pas se vendre. Son désir : avoir une image pro et décrocher ses 3 premiers clients en 90 jours. Objection principale : "Je n\'ai pas le budget pour ça maintenant."'
      },
      amorce: 'Décris-moi ton client idéal en 3-4 phrases. Qui est-il ? Quel est son problème principal ? L\'IA va ensuite t\'aider à affiner chaque dimension.'
    }
  },
  3: {
    titre: 'Raison d\'être & valeurs', emoji: '❤️', avecIA: true,
    intro: 'Les clients n\'achètent pas ce que tu fais, ils achètent POURQUOI tu le fais.',
    pedagogie: {
      concept: {
        titre: 'C\'est quoi la raison d\'être ?',
        texte: 'Ta raison d\'être, c\'est la cause profonde pour laquelle ton entreprise existe, au-delà du profit. C\'est le moteur qui te fait te lever le matin et qui crée une connexion émotionnelle avec tes clients.',
        warning: 'Sans raison d\'être claire, tu es interchangeable. Avec elle, tu deviens inoubliable.'
      },
      framework: {
        titre: 'Le Cercle d\'Or, Simon Sinek',
        items: [
          { icon: '❓', label: 'POURQUOI (ta raison d\'être)', desc: 'La cause que tu sers, ta conviction profonde. Ex: "Je crois que chaque entrepreneur mérite d\'être vu et entendu."' },
          { icon: '⚙️', label: 'COMMENT (ta méthode)', desc: 'Ton approche unique, ta philosophie d\'action. Ce qui te différencie dans la façon dont tu travailles.' },
          { icon: '📦', label: 'QUOI (tes services)', desc: 'Ce que tu vends concrètement. La plupart commencent ici, c\'est l\'erreur. Commence toujours par le POURQUOI.' },
        ]
      },
      exemple: {
        titre: 'Exemple, Apple',
        texte: 'POURQUOI : Nous croyons que tout ce qu\'on fait doit remettre en question le statu quo. COMMENT : En concevant des produits magnifiquement simples. QUOI : Des ordinateurs et des téléphones. → Résultat : des clients qui font la queue des nuits entières.'
      },
      amorce: 'Commence par répondre à cette question : pourquoi as-tu créé cette entreprise ? Pas "pour gagner de l\'argent", quelle cause, quelle conviction profonde t\'a poussé à te lancer ?'
    }
  },
  4: {
    titre: 'Positionnement et promesse', emoji: '🧭', avecIA: true,
    intro: 'Ton identité visuelle, c\'est la première impression avant même qu\'on lise un mot. Elle doit refléter exactement qui tu es.',
    pedagogie: {
      concept: {
        titre: 'L\'identité visuelle, c\'est bien plus qu\'un logo',
        texte: 'Ton identité visuelle est un système cohérent qui communique ta personnalité de marque à chaque point de contact : site, réseaux, documents, packaging, tenue...',
        warning: 'Ne crée pas ton logo avant d\'avoir défini ton persona et ta raison d\'être. Un bon logo est la synthèse de tout ça, pas un point de départ.'
      },
      framework: {
        titre: 'Les 5 composantes de ton identité',
        items: [
          { icon: '🎨', label: 'Palette de couleurs', desc: '3 couleurs max. Chaque couleur a une psychologie : bleu = confiance, or = prestige, vert = croissance, orange = énergie' },
          { icon: '✍️', label: 'Typographie', desc: '2 polices max : une pour les titres (personnalité), une pour le texte (lisibilité). Règle : serif + sans-serif.' },
          { icon: '🔷', label: 'Logo', desc: 'Simple, mémorable, déclinable. Doit fonctionner en noir & blanc et en petit format. Vise l\'intemporel.' },
          { icon: '📸', label: 'Univers photographique', desc: 'Style, lumière, traitement. Cohérence absolue sur tous tes supports. Crée un moodboard de 9 photos.' },
          { icon: '✨', label: 'Ton graphique', desc: 'Illustrations ou non ? Formes géométriques ou organiques ? Épuré ou chargé ? Tout doit être cohérent.' },
        ]
      },
      exemple: {
        titre: 'Questions pour ton brief créatif',
        texte: 'Pense à des marques que tu admires. Quels adjectifs utiliserais-tu pour décrire leur univers visuel ? Luxueux, accessible, chaleureux, tech, naturel, avant-gardiste ? Ces mots sont le point de départ de ton identité.'
      },
      amorce: 'Décris en 3 mots l\'ambiance visuelle que tu veux pour ta marque. Puis cite 2-3 marques dont tu aimes l\'univers visuel, pas pour les copier, mais pour identifier ce qui t\'attire.'
    }
  },
  5: {
    titre: 'Personnalité et voix', emoji: '📢', avecIA: true,
    intro: 'Tu peux avoir la plus belle identité visuelle, si tu ne sais pas quoi dire, personne n\'achètera.',
    pedagogie: {
      concept: {
        titre: 'Ta messagerie, c\'est ta voix de marque',
        texte: 'La messagerie, c\'est l\'ensemble des messages que tu utilises pour parler à ton persona : ton pitch, ta promesse, ton ton de communication, tes accroches. Tout doit être cohérent et résonner immédiatement avec ta cible.',
        warning: 'Si quelqu\'un en dehors de ton secteur ne comprend pas ce que tu fais en 10 secondes, recommence. La clarté est un avantage concurrentiel majeur.'
      },
      framework: {
        titre: 'Les 4 messages clés à maîtriser',
        items: [
          { icon: '⚡', label: 'Le pitch 30 secondes', desc: 'Formule : "J\'aide [persona] à [résultat] grâce à [ton approche unique]." Simple, mémorable, sans jargon.' },
          { icon: '🎯', label: 'La promesse principale', desc: 'En une phrase : ce que tu garantis concrètement. Ex: "Vos premiers 3 clients en 90 jours ou on recommence ensemble."' },
          { icon: '📱', label: 'La bio réseaux sociaux', desc: '3 lignes max : qui tu aides + résultat + appel à l\'action. Inclus un mot-clé pour être trouvé.' },
          { icon: '🌐', label: 'L\'accroche de ton site', desc: 'Le titre principal de ta page d\'accueil : ce que tu fais + pour qui + le bénéfice. Les visiteurs décident en 3 secondes.' },
        ]
      },
      exemple: {
        titre: 'Exemple de pitch réussi',
        texte: '"J\'aide les créateurs d\'entreprise à construire une marque professionnelle en 20 jours, grâce à une méthode guidée par IA, même sans budget communication." → Clara, concret, différenciant, mémorable.'
      },
      amorce: 'Essaie de formuler ton pitch 30 secondes : "J\'aide [qui ?] à [quoi ?] grâce à [comment ?]". Ne cherche pas la perfection, l\'IA va t\'aider à l\'affiner jusqu\'à ce que ce soit vraiment percutant.'
    }
  },
  6: {
    titre: "Construction de ton offre", emoji: "📦", avecIA: true,
    intro: "Les modules gratuits t\'ont permis de poser les fondations. Maintenant on passe aux choses serieuses : construire une offre qui se vend, avec un prix juste et une promesse irresistible. L\'IA va s\'appuyer sur tout ce que tu as deja defini.",
    pedagogie: {
      concept: {
        titre: "Une offre, ce n\'est pas juste un prix",
        texte: "Une offre est un systeme complet : ce que tu livres, comment tu le livres, a qui, pour quel resultat, a quel prix et avec quelle garantie. Chaque element doit etre coherent avec ton persona et ta raison d\'etre.",
        warning: "L\'erreur la plus commune : fixer un prix au hasard ou copier la concurrence. Ton prix doit refleter la valeur creee, pas tes heures de travail."
      },
      framework: {
        titre: "Les 6 composantes d\'une offre solide",
        items: [
          { icon: "🎁", label: "Le coeur de l\'offre", desc: "Ce que tu livres exactement : nombre de sessions, duree, format, livrables tangibles" },
          { icon: "✨", label: "La promesse", desc: "Le resultat specifique et mesurable que ton client va obtenir. Pas un service - une transformation." },
          { icon: "💰", label: "Le prix", desc: "Base sur la valeur creee, pas sur ton temps. Calcule le ROI pour ton client et prix en consequence." },
          { icon: "🛡️", label: "La garantie", desc: "Ce qui reduit le risque percu. Meme partielle, une garantie multiplie les conversions." },
          { icon: "⏰", label: "L\'urgence", desc: "Pourquoi agir maintenant ? Places limitees, bonus temporaires, periode ideale..." },
          { icon: "📈", label: "Les upsells", desc: "Que proposes-tu apres ? Une offre isolee est une opportunite manquee." },
        ]
      },
      exemple: {
        titre: "Exemple d\'offre bien construite",
        texte: "Coaching Lancement 90 jours : 6 sessions de 90min + plan action personnalise + support WhatsApp illimite + template de prospection. Promesse : tes 3 premiers clients ou remboursement integral. Prix : 1497 euros (valeur creee estimee : 5000 euros minimum). Garantie satisfait ou rembourse 30 jours."
      },
      amorce: "Dis-moi ce que tu veux proposer comme offre principale. Je vais m\'appuyer sur ton persona, ta raison d\'etre et ton identite de marque pour t\'aider a la structurer et a definir le bon prix."
    }
  },
  7: {
    titre: "Plan de lancement 90 jours", emoji: "🚀", avecIA: true,
    intro: "Une strategie sans plan d\'execution ne vaut rien. On va construire ensemble ton plan de lancement sur 90 jours : des actions concretes, des objectifs mesurables et des KPIs hebdomadaires. L\'IA va personnaliser ce plan en fonction de toute ta marque.",
    pedagogie: {
      concept: {
        titre: "Pourquoi 90 jours ?",
        texte: "90 jours est la periode ideale pour lancer une activite : assez long pour voir des resultats, assez court pour rester motive et reactif. C\'est la methode OKR utilisee par Google, LinkedIn et des milliers de startups.",
        warning: "Le piege : faire une belle feuille de route et ne jamais l\'executer. Ce plan doit etre realiste, hebdomadaire et mesurable. Pas ambitieux sur le papier et vide dans les faits."
      },
      framework: {
        titre: "La structure OKR sur 90 jours",
        items: [
          { icon: "🎯", label: "L\'Objectif (O)", desc: "Un seul objectif qualitatif et inspirant pour les 90 jours. Ex : Devenir la reference du coaching commercial pour les freelances." },
          { icon: "📊", label: "Les Resultats Cles (KR)", desc: "3 resultats mesurables qui prouvent que l\'objectif est atteint. Ex : 5 clients signes, 500 abonnes LinkedIn, 10 temoignages." },
          { icon: "📅", label: "Mois 1 - Fondations", desc: "Setup : site, reseaux, offre finalisee, premiers contacts. Objectif : etre pret a prospecter." },
          { icon: "🚀", label: "Mois 2 - Lancement", desc: "Prospection active, contenu regulier, premiers appels decouverte. Objectif : premiers clients." },
          { icon: "⚡", label: "Mois 3 - Acceleration", desc: "Optimisation de ce qui fonctionne, referrals, upsells. Objectif : systeme en place." },
          { icon: "📈", label: "KPIs hebdomadaires", desc: "3 indicateurs a suivre chaque semaine sans exception. Ce qui se mesure s\'ameliore." },
        ]
      },
      exemple: {
        titre: "Exemple de plan OKR 90 jours",
        texte: "O : Devenir la coach nutrition preferee des mamans actives en Ile-de-France. KR1 : 5 clientes en accompagnement individuel. KR2 : 200 abonnees Instagram engagees. KR3 : 3 temoignages video. Mois 1 : site + Instagram + 50 prises de contact. Mois 2 : 3 lives, 5 appels decouverte, 1ere cliente. Mois 3 : programme groupe, partenariats, systeme de referral."
      },
      amorce: "Parle-moi de tes ambitions : ou tu veux en etre dans 90 jours ? Combien de clients, quel chiffre d\'affaires, quelle visibilite ? Je vais construire avec toi un plan realiste et adapte a ta situation, en m\'appuyant sur tout ce qu\'on a defini ensemble."
    }
  },
  8: {
    titre: 'Stratégie réseaux sociaux', emoji: '📱', avecIA: true,
    intro: "On va définir ta stratégie réseaux sociaux sur-mesure, en cohérence avec ton persona, ta marque et ton offre. L'IA va analyser tous les réseaux disponibles et ne retenir que ceux qui ont du sens pour toi, avec un plan de contenu concret et un calendrier 30 jours.",
    pedagogie: {
      concept: {
        titre: 'Pourquoi une stratégie et pas juste poster ?',
        texte: "Poster sans stratégie, c'est crier dans le vide. Une vraie stratégie réseaux sociaux, c'est choisir les bons canaux, les bons formats, les bons messages, et les aligner sur ton objectif business.",
        warning: "L'erreur n°1 : être partout et n'engager personne. Mieux vaut exceller sur 2 réseaux que d'être invisible sur 10."
      },
      framework: {
        titre: 'Les 3 niveaux du funnel social',
        items: [
          { icon: '👁️', label: 'Découverte', desc: 'Tu touches des gens qui ne te connaissent pas encore. Objectif : visibilité et notoriété.' },
          { icon: '🤝', label: 'Considération', desc: "Tu crées de la confiance et de l'engagement. Objectif : les convaincre que tu es la bonne solution." },
          { icon: '💰', label: 'Conversion', desc: "Tu transformes l'intérêt en action : prise de contact, achat, inscription." },
          { icon: '🎯', label: 'Piliers de contenu', desc: '5 thématiques récurrentes qui structurent ta prise de parole et fidélisent ton audience.' },
          { icon: '📅', label: 'Plan 30 jours', desc: 'Une semaine type réaliste avec les formats, fréquences et objectifs de chaque post.' },
          { icon: '📊', label: 'KPIs à suivre', desc: 'Les indicateurs concrets pour savoir si ta stratégie fonctionne : portée, engagement, clics, conversions.' },
        ]
      },
      exemple: {
        titre: 'Exemple, Coach nutrition pour mamans actives',
        texte: "Réseaux retenus : Instagram (découverte + communauté) et LinkedIn (crédibilité pro). Piliers : conseils nutrition rapides / coulisses / témoignages clientes / mythes alimentaires / offres. Fréquence : 4 posts Instagram/semaine + 2 LinkedIn. KPIs : taux d'engagement >3%, 50 nouveaux abonnés/mois, 5 DMs qualifiés/semaine."
      },
      amorce: "Dis-moi sur quels réseaux tu es déjà présent(e) et ceux où tu penses que ton audience se trouve. L'IA va analyser tout ton profil de marque et te recommander la stratégie la plus adaptée."
    }
  },
  9: {
    titre: 'Acquisition client',
    emoji: '🎯',
    avecIA: true,
    intro: "On va construire ton système d'acquisition client : canal principal, messages par stade, lead magnet, parcours prospect et métriques. Un plan concret et mesurable pour des clients prévisibles.",
    pedagogie: {
      concept: { titre: "Rendre l'arrivée de clients prévisible", texte: "", warning: "" },
      framework: { titre: '', items: [] },
      exemple: { titre: '', texte: '' },
      amorce: "On construit ensemble ton plan d'acquisition."
    }
  },
  10: {
    titre: 'Fidélisation client',
    emoji: '🔁',
    avecIA: false,
    intro: "Découvre pourquoi fidéliser coûte 5x moins cher qu'acquérir, et construis ton système de fidélisation adapté à ton activité.",
    pedagogie: {
      concept: { titre: 'La fidélisation, le levier ignoré', texte: '', warning: '' },
      framework: { titre: '', items: [] },
      exemple: { titre: '', texte: '' },
      amorce: "Lance ton diagnostic personnalisé."
    }
  },
};

function PedagogieBlock({ pedagogie }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 28 }}>

      {/* Concept */}
      <Card style={{ background: 'var(--light-bl)', border: '1px solid var(--light-bl2)', padding: '18px 20px' }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--navy)', marginBottom: 8 }}>
          📖 {pedagogie.concept.titre}
        </h3>
        <p style={{ fontSize: '0.9rem', color: 'var(--gray-600)', lineHeight: 1.6, marginBottom: pedagogie.concept.warning ? 10 : 0 }}>
          {pedagogie.concept.texte}
        </p>
        {pedagogie.concept.warning && (
          <div style={{ background: 'var(--gold-light)', borderLeft: '3px solid var(--gold)', borderRadius: 'var(--radius-sm)', padding: '8px 12px', fontSize: '0.85rem', color: 'var(--navy)', fontStyle: 'italic' }}>
            ⚠️ {pedagogie.concept.warning}
          </div>
        )}
      </Card>

      {/* Framework */}
      <Card style={{ padding: '18px 20px' }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--navy)', marginBottom: 14 }}>
          🧭 {pedagogie.framework.titre}
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pedagogie.framework.items.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', background: 'var(--navy)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1rem', flexShrink: 0
              }}>
                {item.icon}
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--navy)', marginBottom: 2 }}>{item.label}</div>
                <div style={{ fontSize: '0.83rem', color: 'var(--gray-400)', lineHeight: 1.5 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Exemple */}
      <Card style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', padding: '16px 20px' }}>
        <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: '#2E7D32', marginBottom: 6 }}>
          ✦ {pedagogie.exemple.titre}
        </h3>
        <p style={{ fontSize: '0.85rem', color: '#1B5E20', lineHeight: 1.6 }}>{pedagogie.exemple.texte}</p>
      </Card>

      {/* Amorce */}
      <Card style={{ background: 'var(--gold-light)', border: '1px solid var(--gold)', padding: '14px 18px' }}>
        <p style={{ fontSize: '0.88rem', color: 'var(--navy)', lineHeight: 1.6 }}>
          <strong>💡 Par où commencer :</strong> {pedagogie.amorce}
        </p>
      </Card>
    </div>
  );
}

export function ModulePage() {
  const { num }    = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const moduleNum  = parseInt(num);
  const config     = MODULE_CONFIG[moduleNum];

  const [moduleData, setModuleData] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const [conversation, setConversation] = useState([]);
  const [inputIA, setInputIA]           = useState('');
  const [tourActuel, setTourActuel]     = useState(0);
  const [iaLoading, setIaLoading]       = useState(false);
  const [moduleValide, setModuleValide] = useState(false);
  const [synthese, setSynthese]         = useState('');
  const [saving, setSaving]             = useState(false);
  const [formAnswers, setFormAnswers]   = useState({});
  const [formSoumis, setFormSoumis]     = useState(false);
  const [showPedagogie, setShowPedagogie] = useState(true);
  const [contextePrecedent, setContextePrecedent] = useState([]);
  const chatEndRef = useRef(null);

  useEffect(() => {
    // Charger le module ET le contexte des modules précédents en parallèle
    Promise.all([
      api.getModule(moduleNum),
      api.getDashboard()
    ])
      .then(([moduleData, dashData]) => {
        setModuleData(moduleData);
        if (moduleData.reponseExistante) {
          setModuleValide(true);
          setSynthese(moduleData.reponseExistante.contenu);
          setShowPedagogie(false);
        }
        // Récupérer les réponses des modules précédents pour le contexte IA
        const reponsesPrec = (dashData.reponses || []).filter(r => r.numeroModule < moduleNum);
        setContextePrecedent(reponsesPrec);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [moduleNum]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  if (!config) return <div>Module introuvable</div>;
  if (loading) return <LoadingModule />;

  const sendMessage = async () => {
    if (!inputIA.trim() || iaLoading) return;
    const userMsg = { role: 'user', content: inputIA.trim() };
    const newConv = [...conversation, userMsg];
    setConversation(newConv);
    setInputIA('');
    setIaLoading(true);
    setShowPedagogie(false);
    try {
      const res = await api.chatIA(moduleNum, { conversation: newConv, tourActuel, contextePrecedent });
      setConversation(prev => [...prev, { role: 'assistant', content: res.message }]);
      setTourActuel(res.tourActuel);
      if (res.valide) { setModuleValide(true); setSynthese(res.message); }
    } catch (err) { setError(err.message); }
    finally { setIaLoading(false); }
  };

  // Module 1 : envoyer les 4 réponses du formulaire comme premier message à l'IA pour challenge
  const envoyerFormulaireVersIA = async () => {
    const reponsesManquantes = config.questions.some((q, i) => !formAnswers[i] || formAnswers[i].trim().length < 5);
    if (reponsesManquantes) {
      setError('Réponds à toutes les questions avant de continuer.');
      return;
    }
    const messageInitial = config.questions
      .map((q, i) => `${q.label} : ${formAnswers[i]}`)
      .join('\n\n');
    const userMsg = { role: 'user', content: messageInitial };
    const newConv = [userMsg];
    setConversation(newConv);
    setFormSoumis(true);
    setIaLoading(true);
    try {
      const res = await api.chatIA(moduleNum, { conversation: newConv, tourActuel: 0, contextePrecedent });
      setConversation(prev => [...prev, { role: 'assistant', content: res.message }]);
      setTourActuel(res.tourActuel);
      if (res.valide) { setModuleValide(true); setSynthese(res.message); }
    } catch (err) { setError(err.message); }
    finally { setIaLoading(false); }
  };

  const validerModule = async () => {
    setSaving(true);
    try {
      // Le contenu sauvegardé est toujours la synthèse IA complète (avec les clés structurées)
      // y compris pour le module 1 désormais, qui passe par un dialogue IA après le formulaire initial
      let contenu = synthese;
      if (!contenu || contenu.length < 20) {
        setError('Le module n\'est pas encore complet, termine l\'échange avec l\'IA avant de valider.');
        return;
      }
      await api.validateModule(moduleNum, { contenu, nbToursIA: tourActuel, tokensConsommes: 0 });
      const next = moduleNum + 1;
      if (next <= 8) navigate(`/module/${next}`, { replace: true });
      // Module 8 = dernier : on reste sur la page pour afficher la fiche
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const joursRestants = moduleData?.joursRestants ?? user?.joursRestants ?? 0;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--off-white)' }}>
      {/* NAV */}
      <Navbar showBack={true} />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '40px 24px' }}>

        {/* HEADER MODULE */}
        <div style={{ marginBottom: 28 }} className="animate-fadeUp">
          <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', fontWeight: 500, marginBottom: 4, letterSpacing: '1px' }}>
            MODULE {String(moduleNum).padStart(2, '0')} {config.avecIA && '· Assisté par IA'}
          </div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '1.9rem', color: 'var(--navy)', marginBottom: 10 }}>
            {config.emoji} {config.titre}
          </h1>
          <div style={{ background: 'var(--light-bl)', border: '1px solid var(--light-bl2)', borderRadius: 'var(--radius-md)', padding: '12px 16px' }}>
            <p style={{ fontSize: '0.9rem', color: 'var(--navy)', lineHeight: 1.6 }}>{config.intro}</p>
          </div>
        </div>

        {error && ![1, 2, 3, 4, 5, 8, 9, 10, 11, 12, 13].includes(moduleNum) && <Alert type="error" style={{ marginBottom: 20 }}>{error}</Alert>}

        {/* MODULE 8 — RÉSEAUX SOCIAUX (formation + CTA Léa) */}
        {moduleNum === 8 && (
          <ModuleReseaux
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(8, { contenu, nbToursIA: 0, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
                navigate('/module/9', { replace: true });
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 10 — FIDÉLISATION CLIENT */}
        {moduleNum === 10 && (
          <ModuleFidelisation
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(10, { contenu, nbToursIA: 0, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 9 — ACQUISITION CLIENT */}
        {moduleNum === 9 && (
          <ModuleAcquisition
            moduleValide={moduleValide}
            syntheseExistante={synthese}
            contextePrecedent={contextePrecedent}
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(9, { contenu, nbToursIA: 6, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 1 — IDÉE & VISION (formation + questions + dialogue + fiche) */}
        {moduleNum === 1 && (
          <ModuleIdee
            moduleValide={moduleValide}
            syntheseExistante={synthese}
            contextePrecedent={contextePrecedent}
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(1, { contenu, nbToursIA: conversation.length, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
                navigate('/module/2', { replace: true });
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 2 — PERSONA (formation + quiz + conversation + fiche) */}
        {moduleNum === 2 && (
          <ModulePersona
            moduleValide={moduleValide}
            syntheseExistante={synthese}
            contextePrecedent={contextePrecedent}
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(2, { contenu, nbToursIA: 10, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
                navigate('/module/3', { replace: true });
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 3 - GUIDE */}
        {moduleNum === 3 && (
          <ModuleGuide
            cfg={CFG_RAISON}
            moduleValide={moduleValide}
            syntheseExistante={synthese}
            contextePrecedent={contextePrecedent}
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(3, { contenu, nbToursIA: 10, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
                navigate('/module/4', { replace: true });
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 5 - GUIDE */}
        {moduleNum === 5 && (
          <ModuleGuide
            cfg={CFG_VOIX}
            moduleValide={moduleValide}
            syntheseExistante={synthese}
            contextePrecedent={contextePrecedent}
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(5, { contenu, nbToursIA: 10, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
                navigate('/felicitations', { replace: true });
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 11 - GOOGLE BUSINESS */}
        {moduleNum === 11 && (
          <ModuleGuide
            cfg={CFG_GOOGLE}
            moduleValide={moduleValide}
            syntheseExistante={synthese}
            contextePrecedent={contextePrecedent}
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(11, { contenu, nbToursIA: 10, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
                navigate('/parcours', { replace: true });
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 12 - META */}
        {moduleNum === 12 && (
          <ModuleGuide
            cfg={CFG_META}
            moduleValide={moduleValide}
            syntheseExistante={synthese}
            contextePrecedent={contextePrecedent}
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(12, { contenu, nbToursIA: 10, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
                navigate('/parcours', { replace: true });
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 13 - PREMIERS CLIENTS */}
        {moduleNum === 13 && (
          <ModuleGuide
            cfg={CFG_CLIENTS}
            moduleValide={moduleValide}
            syntheseExistante={synthese}
            contextePrecedent={contextePrecedent}
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(13, { contenu, nbToursIA: 10, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
                navigate('/parcours', { replace: true });
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 4 - POSITIONNEMENT */}
        {moduleNum === 4 && (
          <ModulePositionnement
            moduleValide={moduleValide}
            syntheseExistante={synthese}
            contextePrecedent={contextePrecedent}
            onValidate={async (contenu) => {
              setSaving(true);
              try {
                await api.validateModule(4, { contenu, nbToursIA: 10, tokensConsommes: 0 });
                setSynthese(contenu);
                setModuleValide(true);
                navigate('/module/5', { replace: true });
              } catch(e) { setError(e.message); }
              finally { setSaving(false); }
            }}
          />
        )}

        {/* MODULE 1 — FORMULAIRE INITIAL (avant dialogue IA) — désactivé, géré par ModuleIdee */}
        {moduleNum === 1 && false && !formSoumis && !moduleValide && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }} className="animate-fadeUp">
            {config.questions.map((q, i) => (
              <Card key={i} style={{ padding: '20px 24px' }}>
                <label style={{ display: 'block', fontWeight: 600, color: 'var(--navy)', marginBottom: 10, fontSize: '0.95rem' }}>
                  {q.label}
                </label>
                <textarea
                  value={formAnswers[i] || ''}
                  onChange={e => setFormAnswers(prev => ({ ...prev, [i]: e.target.value }))}
                  placeholder={q.placeholder}
                  rows={3}
                  style={{
                    width: '100%', padding: '11px 14px',
                    border: '1.5px solid var(--gray-200)', borderRadius: 'var(--radius-md)',
                    fontSize: '0.93rem', resize: 'vertical', lineHeight: 1.6,
                    fontFamily: 'var(--font-body)', color: 'var(--gray-800)', outline: 'none'
                  }}
                  onFocus={e => e.target.style.borderColor = 'var(--navy)'}
                  onBlur={e => e.target.style.borderColor = 'var(--gray-200)'}
                />
              </Card>
            ))}
            <BtnPrimary onClick={envoyerFormulaireVersIA} loading={iaLoading} style={{ alignSelf: 'flex-start', padding: '12px 28px' }}>
              Soumettre à l'IA pour validation →
            </BtnPrimary>
          </div>
        )}

        {/* MODULES IA (y compris module 1 après soumission du formulaire) */}
        {config.avecIA && moduleNum !== 2 && moduleNum !== 3 && moduleNum !== 4 && moduleNum !== 5 && moduleNum !== 11 && moduleNum !== 12 && moduleNum !== 13 && moduleNum !== 1 && moduleNum !== 9 && moduleNum !== 8 && moduleNum !== 10 && (formSoumis || moduleNum !== 1) && (
          <div className="animate-fadeUp">

            {/* MODULE DÉJÀ COMPLÉTÉ */}
            {moduleValide && synthese && (
              <div style={{ marginBottom: 20 }}>
                {moduleNum === 4 ? (
                  <PlaquetteIdentite synthese={synthese} prenom={user?.prenom} />
                ) : moduleNum === 2 ? (
                  <FichePersona synthese={synthese} />
                ) : moduleNum === 8 ? (
                  <FicheReseaux synthese={synthese} />
                ) : moduleNum === 1 ? (
                  <FicheIdee synthese={synthese} />
                ) : (
                  <Alert type="success">
                    <strong>✅ Module complété</strong><br />
                    <span style={{ fontSize: '0.88rem', marginTop: 4, display: 'block' }}>{synthese}</span>
                  </Alert>
                )}
              </div>
            )}

            {/* BLOC PÉDAGOGIQUE */}
            {config.pedagogie && showPedagogie && !moduleValide && (
              <div className="animate-fadeUp">
                <PedagogieBlock pedagogie={config.pedagogie} />
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                  <button onClick={() => setShowPedagogie(false)} style={{
                    background: 'transparent', border: 'none', color: 'var(--gray-400)',
                    fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline'
                  }}>
                    Passer directement au dialogue IA →
                  </button>
                </div>
              </div>
            )}

            {/* ZONE DE DIALOGUE */}
            {(!showPedagogie || conversation.length > 0 || moduleValide) && (
              <Card style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
                {/* Messages */}
                <div style={{ maxHeight: 500, overflowY: 'auto', padding: '24px' }}>

                  {conversation.length === 0 && !moduleValide && (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--gray-400)' }}>
                      <div style={{ fontSize: '2rem', marginBottom: 10 }}>🤖</div>
                      <p style={{ fontSize: '0.88rem', lineHeight: 1.6, maxWidth: 380, margin: '0 auto', fontStyle: 'italic' }}>
                        {config.pedagogie?.amorce || 'Commence par me partager ta première réflexion sur ce module.'}
                      </p>
                    </div>
                  )}

                  {conversation.map((msg, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      marginBottom: 16
                    }} className="animate-fadeIn">
                      {msg.role === 'assistant' && (
                        <div style={{
                          width: 30, height: 30, borderRadius: '50%', background: 'var(--navy)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.75rem', color: 'white', marginRight: 8, flexShrink: 0, marginTop: 4
                        }}>IA</div>
                      )}
                      <div style={{
                        maxWidth: '78%', padding: '12px 16px',
                        borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        background: msg.role === 'user' ? 'var(--navy)' : 'var(--gray-100)',
                        color: msg.role === 'user' ? 'white' : 'var(--gray-800)',
                        fontSize: '0.9rem', lineHeight: 1.6,
                        borderLeft: msg.role === 'assistant' && msg.content.startsWith('VALIDÉ:') ? '3px solid var(--success)' : 'none'
                      }}>
                        {msg.content.replace('VALIDÉ: ', '')}
                      </div>
                    </div>
                  ))}

                  {iaLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0' }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: 'white' }}>IA</div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {[0,1,2].map(i => (
                          <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--gray-200)', animation: `pulse 1.2s ease ${i * 0.2}s infinite` }} />
                        ))}
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Input */}
                {!moduleValide && (
                  <div className="gm-input-wrap" style={{ borderTop: '1px solid var(--gray-100)', padding: '14px 18px', display: 'flex', gap: 10 }}>
                    <textarea
                      value={inputIA}
                      onChange={e => setInputIA(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                      placeholder="Écris ta réponse"
                      rows={2}
                      style={{
                        flex: 1, padding: '10px 14px', border: '1.5px solid var(--gray-200)',
                        borderRadius: 'var(--radius-md)', resize: 'none', fontSize: '0.9rem',
                        fontFamily: 'var(--font-body)', lineHeight: 1.5, outline: 'none'
                      }}
                      onFocus={e => e.target.style.borderColor = 'var(--navy)'}
                      onBlur={e => e.target.style.borderColor = 'var(--gray-200)'}
                    />
                    <BtnPrimary onClick={sendMessage} disabled={!inputIA.trim() || iaLoading} style={{ padding: '10px 16px', alignSelf: 'flex-end' }}>
                      {iaLoading ? <Spinner size={16} color="white" /> : '→'}
                    </BtnPrimary>
                  </div>
                )}
                {!moduleValide && <div className="gm-spacer" />}
              </Card>
            )}

            {/* Compteur tours */}
            {tourActuel > 0 && !moduleValide && (
              <p style={{ fontSize: '0.78rem', color: 'var(--gray-400)', textAlign: 'right', marginBottom: 10 }}>
                Échange {tourActuel} / 8
              </p>
            )}

            {/* Bouton de secours : forcer validation si l'IA a répondu mais VALIDÉ non détecté */}
            {!moduleValide && tourActuel >= 2 && conversation.length > 0 && (
              <div style={{ textAlign: 'right', marginBottom: 8 }}>
                <button onClick={async () => {
                  const dernierMsg = conversation.filter(m => m.role === 'assistant').pop();
                  if (!dernierMsg) return;
                  setSynthese(dernierMsg.content);
                  setModuleValide(true);
                  try {
                    await api.validateModule(moduleNum, { contenu: dernierMsg.content, nbToursIA: tourActuel, tokensConsommes: 0 });
                  } catch(e) { console.error('Erreur sauvegarde forcée:', e); }
                }} style={{
                  background: 'transparent', border: 'none', color: 'var(--gray-400)',
                  fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline'
                }}>
                  L'IA a terminé mais le bouton n'apparaît pas ? Cliquez ici →
                </button>
              </div>
            )}

            {/* Boutons action */}
            {moduleValide && (
              <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
                <BtnGold onClick={validerModule} style={{ padding: '12px 28px' }}>
                  {saving ? <Spinner size={16} color="var(--navy)" /> : 'Valider et continuer →'}
                </BtnGold>
                <BtnOutline onClick={() => {
                  if (user?.premiumDebloque) {
                    setModuleValide(false);
                    setSynthese('');
                    setShowPedagogie(false);
                    setConversation([]);
                    setTourActuel(0);
                  } else {
                    alert('🔒 Fonctionnalité réservée aux membres Premium.\nPrends rendez-vous avec Stratvente pour débloquer l\'accès complet.');
                  }
                }}>
                  {user?.premiumDebloque ? 'Retravailler' : '🔒 Retravailler'}
                </BtnOutline>
              </div>
            )}

            {/* Bouton réafficher la pédagogie */}
            {!showPedagogie && !moduleValide && conversation.length === 0 && (
              <button onClick={() => setShowPedagogie(true)} style={{
                background: 'transparent', border: 'none', color: 'var(--gray-400)',
                fontSize: '0.82rem', cursor: 'pointer', textDecoration: 'underline', marginTop: 8
              }}>
                ← Revoir le guide
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingModule() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)' }}>
      <Spinner size={32} />
    </div>
  );
}
// cache bust Fri Jun  5 19:04:34 UTC 2026
const MARKER='BRANDSTARTER_PLAQUETTE_1780686312';
